import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { intersection, difference, union } from 'lodash';
import { connectRefinementList, connectNumericMenu } from 'react-instantsearch-dom';

import ranges from 'Helpers/Geo/price-ranges';
import { currencyCode } from 'Helpers/Geo/data';
import SiteSlider from 'Components/Utility/SiteSlider/SiteSlider';
import { SteamDeckIcon, BundleIcon, VrIcon, GamesUnderIcon } from './CustomFilterIcons';

import './CustomFilters.scss';

const messages = defineMessages({
  bundle: {
    id: 'custom-filters.bundle',
    defaultMessage: 'Bundle',
  },
  steamDeck: {
    id: 'custom-filters.steam-deck',
    defaultMessage: 'Steam Deck Verified',
  },
  vr: {
    id: 'custom-filters.vr',
    defaultMessage: 'VR Support',
  },
  gamesUnder: {
    id: 'custom-filters.games-under',
    defaultMessage: 'Games and Under',
  },
});

// Function to convert currency code to symbol
const getCurrencySymbol = (code) => {
  const symbols = {
    USD: '$',
    GBP: '£',
    EUR: '€',
    AUD: '$',
    CAD: '$',
    RUB: '₽',
    JPY: '¥',
  };
  return symbols[code] || code;
};

const CustomFilter = connectRefinementList(({ values, label, currentRefinement, refine, icon }) => {
  const handleButtonClick = () => {
    const itemsToRemove = intersection(currentRefinement, values);
    const allItems = union(currentRefinement, values);
    const newRefinement = difference(allItems, itemsToRemove);

    refine(newRefinement);
  };

  return (
    <button
      className="filter-button"
      onClick={handleButtonClick}
    >
      <div className="filter-button-icon">
        {icon}
      </div>
      {label}
    </button>
  );
});

const CustomPriceFilter = connectNumericMenu(({ items, refine, icon, label }) => {
  // Find the smallest option
  const option = items[0];
  if (!option) return null;

  return (
    <button
      className="filter-button"
      onClick={() => {
        refine(option.value);
      }}
    >
      <div className="filter-button-icon">
        {icon}
      </div>
      {label}
    </button>
  );
});

const CustomFilters = () => {
  const { formatMessage } = useIntl();

  const customPriceLabel = `${getCurrencySymbol(currencyCode)}${ranges[0].end} ${formatMessage(messages.gamesUnder)}`;

  const sliderSettings = {
    xs: {
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
    },
    sm: {
      slidesToShow: 2,
      slidesToScroll: 1,
      arrows: false,
    },
    md: {
      slidesToShow: 3,
      slidesToScroll: 1,
      arrows: false,
    },
    lg: {
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: false,
    },
    xl: {
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: false,
    },
    xxl: {
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: false,
    },
  };

  const filterItems = [
    {
      key: 'bundle-filter',
      attribute: 'display_type',
      values: ['bundle'],
      icon: <BundleIcon />,
      label: formatMessage(messages.bundle),
    },
    {
      key: 'steam-deck-filter',
      attribute: 'steam_deck_support',
      values: ['verified', 'playable'],
      icon: <SteamDeckIcon />,
      label: formatMessage(messages.steamDeck),
    },
    {
      key: 'vr-filter',
      attribute: 'features',
      values: ['VR support'],
      icon: <VrIcon />,
      label: formatMessage(messages.vr),
    },
  ];

  return (
    <div className="custom-filters-container">
      <SiteSlider sliderClass="custom-filters-slider" sliderSettings={sliderSettings}>
        <CustomPriceFilter
          key="price-filter"
          attribute={`price.${currencyCode}`}
          items={ranges}
          icon={<GamesUnderIcon />}
          label={customPriceLabel}
        />
        {filterItems.map(filter => (
          <CustomFilter
            key={filter.key}
            attribute={filter.attribute}
            values={filter.values}
            icon={filter.icon}
            label={filter.label}
          />
        ))}
      </SiteSlider>
    </div>
  );
};

CustomFilters.propTypes = {
  searchState: PropTypes.shape({}).isRequired,
};

export default CustomFilters;
